import { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  message,
  Spin,
  Tooltip,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { Moon } from "lucide-react";
import dayjs from "dayjs";

import useCompanies from "../../hooks/useCompanies";
import useUsers from "../../hooks/useUsers";
import useGroups from "../../hooks/useGroups";
import CoordinatorFormModal from "../CoordinatorFormModal";

const { RangePicker } = DatePicker;

function GroupFormModal({ visible, initialDate, onClose, onSuccess }) {
  const { getCompanies, companies, loading: companyLoading } = useCompanies();
  const { getUsers, users } = useUsers();
  const { createGroup, loading } = useGroups();

  const [coordinatorDisabled, setCoordinatorDisabled] = useState(true);
  const [params, setParams] = useState({});
  const [nights, setNights] = useState(0);
  const [selectedCoordinator, setSelectedCoordinator] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      getCompanies();
      form.resetFields();
      setCoordinatorDisabled(true);
      setSelectedCoordinator(null);
      setNights(0);

      // Pre-fill with initial date if provided
      if (initialDate) {
        const checkInDate = dayjs(initialDate);
        const checkOutDate = checkInDate.add(3, "day"); // Default 3 nights
        setDateRange([checkInDate, checkOutDate]);
        setNights(3);
        form.setFieldsValue({
          dateRange: [checkInDate, checkOutDate],
        });
      } else {
        setDateRange(null);
      }
    }
  }, [visible, initialDate]);

  useEffect(() => {
    if (params.company_id) {
      getUsers(params);
    }
  }, [params]);

  const enableCoordinator = (company) => {
    if (company) {
      setParams({ company_id: company.value });
      setCoordinatorDisabled(false);
    } else {
      setCoordinatorDisabled(true);
    }
  };

  const handleEditCoordinator = () => {
    const coordinatorId =
      selectedCoordinator || form.getFieldValue("coordinator");
    if (!coordinatorId) {
      message.warning("Por favor selecciona un coordinador primero");
      return;
    }

    const baseUrl = process.env.REACT_APP_API_URL;
    const editUrl = `${baseUrl}admin/users/customuser/${coordinatorId}/change/`;
    window.open(editUrl, "_blank");
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);

    if (dates && dates.length === 2) {
      const [startDate, endDate] = dates;
      if (endDate) {
        const nightCount = endDate.diff(startDate, "day");
        setNights(nightCount);
      }
      form.setFieldsValue({ dateRange: dates });
    } else {
      setNights(0);
      form.setFieldsValue({ dateRange: null });
    }
  };

  const onFinish = async (values) => {
    const { dateRange } = values;

    const [checkInDate, checkOutDate] = dateRange.map((date) =>
      dayjs(date).startOf("day").format("YYYY-MM-DD")
    );

    const formData = {
      ...values,
      checkInDate,
      checkOutDate,
      company: values.company.value,
    };

    delete formData.dateRange;

    const result = await createGroup(formData);
    if (result) {
      form.resetFields();
      onSuccess?.();
      onClose();
    }
  };

  const onFinishFailed = () => {
    message.warning("Formulario inválido");
  };

  return (
    <Modal
      title="Crear Nuevo Grupo"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Spin spinning={loading.post}>
        <Form
          form={form}
          name="groupFormModal"
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          className="mt-4"
        >
          <Form.Item
            label="Empresa"
            name="company"
            rules={[
              {
                required: true,
                message: "Por favor seleccione una compañía.",
              },
            ]}
          >
            <Select
              showSearch
              labelInValue
              optionFilterProp="label"
              loading={companyLoading.get}
              options={companies.results?.map((company) => ({
                label: company.name,
                value: company.id,
              }))}
              onSelect={(company) => {
                enableCoordinator(company);
              }}
              placeholder="Seleccionar empresa"
            />
          </Form.Item>

          <div className="flex w-full items-end gap-2">
            <Form.Item name="coordinator" className="flex-1" label="Coordinador">
              <Select
                disabled={coordinatorDisabled}
                options={users}
                fieldNames={{ label: "firstName", value: "id" }}
                optionRender={(option) =>
                  `${option.data.firstName} ${option.data.lastName}`
                }
                labelRender={(label) => {
                  if (!label) return "";
                  const user = users.find((u) => u.id === label.value);
                  return `${user?.firstName} ${user?.lastName}`;
                }}
                placeholder="Seleccionar coordinador"
                onChange={(value) => {
                  setSelectedCoordinator(value);
                }}
              />
            </Form.Item>

            <Tooltip title="Editar coordinador">
              <Button
                type="default"
                icon={<EditOutlined />}
                onClick={handleEditCoordinator}
                disabled={!selectedCoordinator}
                className="mb-6"
              />
            </Tooltip>

            <div className="mb-6">
              <CoordinatorFormModal
                company={form.getFieldValue("company")}
                onCreate={(coordinator) => {
                  getUsers(params);
                  form.setFieldsValue({
                    coordinator: coordinator.id,
                  });
                  setSelectedCoordinator(coordinator.id);
                }}
                disabled={!form.getFieldValue("company")}
              />
            </div>
          </div>

          <Form.Item
            label="Rango de Fechas"
            name="dateRange"
            rules={[
              {
                required: true,
                message: "Por favor seleccione el rango de fechas",
              },
            ]}
          >
            <div className="flex items-center">
              <RangePicker
                placeholder={["Fecha de entrada", "Fecha de salida"]}
                className="flex-grow"
                value={dateRange}
                onChange={handleDateRangeChange}
                onCalendarChange={handleDateRangeChange}
              />
              {nights > 0 && (
                <div className="ml-3 flex items-center text-gray-500">
                  <Moon size={16} className="mr-1" />
                  <span>
                    {nights} {nights === 1 ? "noche" : "noches"}
                  </span>
                </div>
              )}
            </div>
          </Form.Item>

          <Form.Item label="Observación" name="observation">
            <Input.TextArea placeholder="Ingrese una observación" />
          </Form.Item>

          <Form.Item className="mb-0 flex justify-end">
            <Button onClick={onClose} className="mr-2">
              Cancelar
            </Button>
            <Button type="primary" htmlType="submit" loading={loading.post}>
              Crear Grupo
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
}

export default GroupFormModal;
